import * as Sentry from '@sentry/gatsby';
import classNames from 'classnames';
import { useFormik } from 'formik';
import {
    KeyboardEvent,
    memo,
    MutableRefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { object, string } from 'yup';

import Button from '~components/Button/Button';
import Dialog from '~components/Dialog/Dialog';
import { usePageContext } from '~components/PageDataProvider/PageDataProvider';
import { getLocationByZip } from '~components/ZipCodeForm/util';
import MarketoForm from '~globals/Marketo/marketoForm';
import { EMAIL_REGEXP, generalRedirect } from '~globals/common';
import { fireTrackEvent } from '~globals/tracking';
import { useAppSelector } from '~hooks/useRedux';
import useWindowSize from '~hooks/useWindowSize';
import errorIcon from '~images/common/error-icon.svg';
import InfoIcon from '~images/inline/info.inline.svg';

import Spinner from '../../Spinner/Spinner';

import { createLead } from './query';
import useAllianceId from '~hooks/useAllianceId';

type Props = {
    uid: string;
    mktoFormId?: number;
    isMobileSideBySide?: boolean;
    isStacked?: boolean;
    classes?: string;
    buttonText?: string;
    waTracking?: string;
    errorTextColor?: string;
    leadInfo?: { advertiseSource: string; leadStatus?: string };
    leadTrackingAction?: { page: { action: string } };
    qaButton?: string;
    inputClasses?: string;
    buttonClasses?: string;
    beforeSubmit?: () => void;
    onSuccess?: () => void;
    onError?: () => void;
    ariaLabel?: string;
    buttonStyletype?: 'primary' | 'secondary' | 'reskin' | 'orange' | 'link' | 'rebrandAction';
    disabled?: boolean;
    labelTextColor?: string;
    formMarginTop?: string;
    isLabelHidden?: boolean;
    leadtype?: string;
    fixedallianceid?: string;
    ctacopy?: string;
};

type SelectOptions = {
    label: string;
    value: string;
    selected?: boolean;
};

type InputProps = {
    id: string;
    name: string;
    label: string;
    title?: string;
    placeholder?: string;
    tooltip?: boolean;
    disabled: boolean;
    required: boolean;
    minChar?: number;
    maxChar?: number;
    autoComplete?: string;
    type: string;
    qaInput: string;
    ariaLabel?: string;
    options?: SelectOptions[];
    onKeyUp?: (evt: KeyboardEvent<HTMLInputElement>) => void;
    invalidMsg?: string;
    fullWidth?: boolean;
    quarterWidth?: boolean;
};

interface FormValues {
    email: string;
    zip: string;
    [key: string]: string;
}

interface apiError {
    type?: string;
    message?: string;
    errorType?: string;
}

export const ZipEmailLeadResponseDataSchema = object({
    email: string().matches(EMAIL_REGEXP, 'Invalid email').required('Required'),
    zip: string()
        .min(5, 'Too Short!')
        .max(5, 'Too Long!')
        .required('Required')
        .test('is-valid-zip', 'Invalid zip', async (value) => {
            if (!value) {
                return false;
            }
            const response = await getLocationByZip(value);
            return !response?.data?.areaByZipCode?.hasError;
        }),
});

const ZipEmailLeadResponseData = ({
    ariaLabel,
    beforeSubmit = () => undefined,
    buttonClasses = '',
    buttonStyletype,
    buttonText = 'Get a quote',
    classes = '',
    disabled = false,
    formMarginTop,
    labelTextColor,
    errorTextColor = 'text-red',
    inputClasses = '',
    isLabelHidden,
    isMobileSideBySide = false,
    isStacked = false,
    mktoFormId = 1004,
    onError = () => undefined,
    onSuccess = () => undefined,
    qaButton = 'get-a-quote',
    uid,
    waTracking = '',
    ctacopy = '',
}: Props) => {
    const IFP_MARKETO_FORM_ID = 1134;
    const INITIAL_VALUES = useRef<FormValues>({
        email: '',
        zip: useAppSelector((state) => state.global.geoLocation?.zip),
    });
    const tooltip = useRef() as MutableRefObject<HTMLButtonElement> | null;
    const [id] = useState(() => uid || `email-${Math.random().toString(36).substring(2, 9)}`);

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [version, setVersion] = useState('a');
    const [inputZip, setInputZip] = useState<string>();
    const [wasSubmitted, setWasSubmitted] = useState(false);

    const { location } = usePageContext();
    const marketoFormId = useAppSelector((state) => state.global.marketoFormId);
    const trackingStatus = useAppSelector((state) => state.global.trackingStatus);
    const { isMobile } = useWindowSize();
    const isStatesPage = location.pathname.includes('-health-insurance/');
    let isEmailZipForm = false;
    const allid = useAllianceId(isStatesPage);

    const mktoFormIdPreped = useCallback(() => {
        if (isStatesPage) return IFP_MARKETO_FORM_ID;

        return marketoFormId ? parseInt(marketoFormId) : mktoFormId;
    }, [isStatesPage, marketoFormId]);

    const mktoForm = new MarketoForm(mktoFormIdPreped());

    const findTestVersion = (attempts = 0) => {
        setTimeout(() => {
            if (attempts > 20) {
                return;
            }
            if (typeof window !== 'undefined' && window.ehimTest) {
                setVersion(window.ehimTest);
            } else {
                findTestVersion(attempts + 1);
            }
        }, 200);
    };

    const trackingComponentName = 'Zip + Email Lead Form';
    if (!waTracking) waTracking = trackingComponentName + ': Get a Quote';

    if (waTracking === 'Zip + Email Lead Form: Get a Quote') {
        isEmailZipForm = true;
    }

    useEffect(() => {
        findTestVersion();
    }, []);

    useEffect(() => {
        setTimeout(() => {
            mktoForm.init();
        }, 1000);
    }, []);

    const handleSubmit = async (values: FormValues): Promise<void> => {
        beforeSubmit();

        if (isLoading) return;

        setIsLoading(true);
        setInputZip(values.zip);

        fireTrackEvent({
            page: { action: process.env.GATSBY_ANALYTICS_SITE_GROUP + ':lead form:get started' },
            events: ['leadCreated', 'save_results'],
        });

        try {
            const emailStatus = isStatesPage ? 'Active' : undefined;
            const leadType = isStatesPage ? 'SE' : undefined;
            const leadStatus = isStatesPage ? 'New' : undefined;
            mktoForm.mktoFormSubmit(values.email, allid, emailStatus, leadType, leadStatus);
        } catch (err) {
            let sentryError;
            if (typeof err === 'string') {
                sentryError = new Error(err);
            } else if (err instanceof Error) {
                sentryError = new Error(err.message);
            }
            if (process.env.GATSBY_EH_ENV === 'prod') {
                Sentry.captureException(sentryError, {
                    tags: {
                        site: process.env.GATSBY_SITE_NAME,
                        component: 'ZipEmailLeadResponseData',
                        event: 'mktoFormSubmit',
                    },
                });
            }
        }

        const leadRequest: ZipEmailLeadRequestArgs = {
            email: values.email,
            zip: values.zip,
            trackingStatus: isStatesPage ? 'L37' : trackingStatus,
        };

        try {
            await createLead(leadRequest);
            onSuccess();
            setWasSubmitted(true);
        } catch (err: unknown) {
            relayApiErrorToFormik(err as apiError);
            let sentryError;
            if (typeof err === 'string') {
                sentryError = new Error(err);
                console.error('Lead error:', err);
            } else if (err instanceof Error) {
                sentryError = new Error(err.message);
                console.error('Lead error:', err.message);
            }
            if (process.env.GATSBY_EH_ENV === 'prod') {
                Sentry.captureException(sentryError, {
                    tags: {
                        site: process.env.GATSBY_SITE_NAME,
                        component: 'ZipEmailLeadResponseData',
                        event: 'createLead',
                    },
                });
            }
            onError();
        } finally {
            setIsLoading(false);
        }
    };

    const relayApiErrorToFormik = (err: apiError) => {
        if (err.type === 'PARAMETER_INVALID') {
            if (err.message?.includes('{phone=error}')) {
                formik.setErrors({ phone: 'Invalid value' });
            }
            if (err.message?.includes('{zip=error}')) {
                formik.setErrors({ zip: 'Invalid value' });
            }
            if (err.message?.includes('{employees=error}')) {
                formik.setErrors({ employees: 'Invalid value' });
            }
        }
    };

    const handleQuotesRedirect = useCallback(() => {
        generalRedirect(
            `${process.env.GATSBY_SITE_URL}/individual-family-health-insurance/plans?zipCode=${inputZip}`,
            allid
        );
    }, [inputZip, allid]);

    const formik = useFormik({
        initialValues: INITIAL_VALUES.current,
        validationSchema: ZipEmailLeadResponseDataSchema,
        validateOnBlur: true,
        onSubmit: handleSubmit,
    });

    const INPUTS = useMemo(() => {
        const inputList: InputProps[] = [
            {
                id: `email_${id}`,
                name: 'email',
                label: 'Email',
                disabled: disabled,
                required: true,
                autoComplete: 'email',
                type: 'text',
                qaInput: 'email-textbox',
                ariaLabel,
                invalidMsg: 'Invalid email address',
                fullWidth: true,
            },
            {
                id: `zip_${id}`,
                name: 'zip',
                label: 'Zip code',
                tooltip: true,
                disabled: disabled,
                required: true,
                minChar: 5,
                maxChar: 5,
                autoComplete: 'postal-code',
                type: 'text',
                qaInput: 'zip-textbox',
                ariaLabel,
                invalidMsg: 'Invalid zip code',
                fullWidth: true,
            },
        ];

        return inputList;
    }, [formik.errors, formik.isValid]);

    const ZipMoreInfo = memo(() => (
        <>
            <button
                type="button"
                className="ml-1 icon-info w-4 h-4 bg-no-repeat"
                aria-label="More info about zip code"
                onClick={() => setIsDialogOpen(!isDialogOpen)}
                ref={tooltip}
                data-qa="zip-email-lead-form-more-info"
                aria-expanded={isDialogOpen}
            >
                <InfoIcon className="text-icon-info" />
            </button>
            <Dialog
                content="Your ZIP Code allows us to filter for Medicare plans in your area."
                dataQa="zip-email-lead-form"
                guid="zip-email-lead-form"
                isOpen={isDialogOpen}
                onClick={() => setIsDialogOpen(false)}
                parentRef={tooltip as MutableRefObject<HTMLButtonElement>}
            />
        </>
    ));
    ZipMoreInfo.displayName = 'ZipMoreInfo';

    const SubmissionMessage = memo(() => (
        <>
            <h3 className="mb-3 lg:m-0 font-bold font-poppins text-24 leading-32 md:text-32 md:leading-40 lg:text-24 lg:leading-32 text-center w-full">
                You’re signed up!
            </h3>
            <p className="mb-0 lg:mb-3 font-semibold font-poppins text-18 leading-28 md:text-20 lg:text-24 lg:leading-32 text-center w-full">
                Now let’s find a plan for you.
            </p>
            <button
                type="button"
                rel="noreferrer noopener"
                className="text-primary font-semibold font-poppins text-18 leading-28 md:text-20 lg:text-24 lg:leading-32 text-center w-full underline"
                onClick={handleQuotesRedirect}
            >
                Browse plans in your area
            </button>
        </>
    ));
    SubmissionMessage.displayName = 'SubmissionMessage';

    return (
        <div
            className={classNames(
                'flex flex-col items-center justify-start',
                'h-full md:px-12 mx-auto w-full',
                wasSubmitted ? 'justify-center' : 'justify-start'
            )}
        >
            {wasSubmitted ? (
                <SubmissionMessage />
            ) : (
                <>
                    <form
                        noValidate
                        className={classNames(
                            classes,
                            'grid grid-cols-4 auto-rows-min gap-x-7 gap-y-3 w-full md:flex md:flex-col'
                        )}
                        onSubmit={formik.handleSubmit}
                    >
                        {INPUTS.map((item) => {
                            const isFieldValid = !(
                                formik.touched[item.name] && formik.errors[item.name]
                            );
                            return (
                                <div
                                    key={item.id}
                                    className={classNames(
                                        inputClasses,
                                        'grid grid-cols-1 grid-rows-3-min-content items-start col-span-4 md:col-span-2 gap-y-1 w-full',
                                        item.fullWidth && 'lg:col-span-4',
                                        item.quarterWidth && 'lg:col-span-1'
                                    )}
                                >
                                    <div className="flex items-start justify-start relative w-full">
                                        <label
                                            htmlFor={item.id}
                                            className={classNames(
                                                'font-poppins text-14 leading-20',
                                                `text-${labelTextColor || '[#F0F0F0]'}`,
                                                isLabelHidden && 'sr-only'
                                            )}
                                        >
                                            {item.label}
                                        </label>
                                        {item.tooltip && <ZipMoreInfo />}
                                    </div>
                                    <input
                                        className={classNames(
                                            'bg-white border rounded placeholder-gray text-black h-12 w-full font-poppins',
                                            'duration-500 ease-in-out transition-colors',
                                            isFieldValid
                                                ? 'border-gray'
                                                : 'border-red bg-red-lightest border-2 mb-1',
                                            isMobile && isMobileSideBySide
                                                ? 'p-2 leading-5'
                                                : 'p-3 leading-22',
                                            `mt-${formMarginTop || '0'} z-10`
                                        )}
                                        id={item.id}
                                        name={item.name}
                                        data-testid={item.name}
                                        autoComplete={item.autoComplete}
                                        onChange={(evt) => {
                                            if (item.maxChar) {
                                                if (evt.target.value?.length <= item.maxChar) {
                                                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                                                    formik.setFieldValue(
                                                        item.name,
                                                        evt.target.value
                                                    );
                                                }
                                            } else {
                                                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                                                formik.setFieldValue(item.name, evt.target.value);
                                            }
                                        }}
                                        onBlur={formik.handleBlur}
                                        onKeyUp={item.onKeyUp}
                                        type={item.type}
                                        value={formik.values[item.name]}
                                        data-qa={item.qaInput}
                                        aria-label={item.ariaLabel}
                                        placeholder={item.placeholder}
                                        disabled={item.disabled}
                                        required={item.required}
                                        min={item.minChar}
                                        max={item.maxChar}
                                    />
                                    <div
                                        className={classNames(
                                            'duration-500 ease-in-out transition-all',
                                            isFieldValid ? 'h-0 invisible' : 'h-5 visible',
                                            'flex items-center justify-start overflow-hidden w-full'
                                        )}
                                        data-qa={`${item.name}-error-message`}
                                    >
                                        <img
                                            src={errorIcon}
                                            alt=""
                                            aria-hidden
                                            width={16}
                                            className="mr-1"
                                            data-qa="error-message-icon"
                                            loading="lazy"
                                        />
                                        <span
                                            className={`text-sm font-poppins ${errorTextColor}`}
                                            data-qa="error-message-text"
                                            aria-live="polite"
                                        >
                                            {formik.errors[item.name]}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                        <Button
                            type="submit"
                            data-wa-link={waTracking}
                            data-qa={qaButton}
                            className={classNames(
                                buttonClasses,
                                (!isStacked || isMobileSideBySide) && 'col-span-4',
                                isMobile && isMobileSideBySide ? '!p-2 !h-auto' : '',
                                isEmailZipForm ? ' md:max-w-lg' : ' md:max-w-[17.625rem]',
                                ' mt-3 mx-auto w-full'
                            )}
                            styletype={
                                buttonStyletype !== undefined
                                    ? buttonStyletype
                                    : version !== 'b'
                                      ? 'primary'
                                      : 'orange'
                            }
                            disabled={disabled}
                        >
                            <span className="font-poppins">
                                {isLoading ? (
                                    <Spinner color="white" />
                                ) : ctacopy ? (
                                    ctacopy
                                ) : buttonText ? (
                                    buttonText
                                ) : (
                                    'Get a quote'
                                )}
                            </span>
                        </Button>
                    </form>
                    <p className="leading-4 mt-6 text-12 w-full">
                        By entering your information above and clicking this button, you agree that
                        we may contact you at the phone number or email address you provide in this
                        form and that any such calls may use an automatic telephone dialing system
                        or an artificial or prerecorded voice to deliver messages that may
                        constitute telemarketing. This is not a condition of purchase. Our{' '}
                        <a
                            className="text-primary"
                            target="_blank"
                            rel="noopener noreferrer"
                            href={`https://www.${process.env.GATSBY_SUBDOMAIN}ehealthinsurance.com/about-ehealth/terms-of-use/`}
                        >
                            Terms of Use
                        </a>
                        {' and '}
                        <a
                            className="text-primary"
                            target="_blank"
                            rel="noopener noreferrer"
                            href={`https://www.${process.env.GATSBY_SUBDOMAIN}ehealthinsurance.com/about-ehealth/privacy-policy/`}
                        >
                            Privacy Policy
                        </a>{' '}
                        apply.
                    </p>
                </>
            )}
        </div>
    );
};

export default ZipEmailLeadResponseData;
